/**
 * Copyright(c) 2022 Fenix Outdoor International AG.
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Fenix ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Fenix.
 *
 */
import React, { useState } from "react";
import PropTypes from "prop-types";
import Row from "../../../common/core/Row";
import Col from "../../../common/core/Col";
import Input from "../../../common/core/Input";
import Button from "../../../common/core/Button";
import Tooltip from "../../../common/core/Tooltip";
import AddSeason from "./AddSeason";
import Permission from "../../../common/security/Permission";
import Rules from "../../../common/security/PermissionRules";

const Search = (props) => {
  const {
    addModal,
    toggleAddModal,
    handleCloseAddModal,
    season,
    handleChange,
    submitSeasonForm,
    handleSubmit,
    register,
    errors,
    handleDateChange,
    searchTerm,
    handleSearchTerm,
    handleSearch,
    handleKeyPress,
    message,
    addSeasonInProgress,
  } = props;

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const SearchTooltip = () => setTooltipOpen(!tooltipOpen);

  return (
    <Row>
      <Col md="8">
        <Permission
          allowed={Rules.Control.SEARCH_SEASON}
        >
          <Row className="g-0 align-items-center">
            <Col className="pe-0 position-relative">
              <Input
                type="text"
                size="lg"
                value={searchTerm}
                onChange={handleSearchTerm}
                onKeyPress={(event) => handleKeyPress(event)}
                placeholder="Search by Season ID"
              />
              <i className="search-icon"> </i>
            </Col>
            <Col xs="auto" className="ps-0">
              <Button color="primary" onClick={handleSearch} className="btn-search" size="lg">
                Search
              </Button>
            </Col>
            <Col xs="auto" className="ms-2">
              <i className="fal fa-info-circle h5 text-danger" id="TooltipWildcard"> </i>
              <Tooltip
                placement="bottom"
                isOpen={tooltipOpen}
                target="TooltipWildcard"
                toggle={SearchTooltip}
                className="tooltip-lg bs-tooltip-bottom"
              >
                <p className="text-start mb-3">
                  You can do wildcard searches also:
                </p>
                <p className="text-start mb-3">
                  <Row className="g-2">
                    <Col xs="auto">
                      <i className="fal fa-share-all fw-bold"> </i>
                    </Col>
                    <Col>
                      FW* will show all seasons whose ID starts with FW
                    </Col>
                  </Row>
                </p>
                <p className="text-start mb-3">
                  <Row className="g-2">
                    <Col xs="auto">
                      <i className="fal fa-share-all fw-bold"> </i>
                    </Col>
                    <Col>
                      *FW will show all seasons whose ID ends with FW
                    </Col>
                  </Row>
                </p>
                <p className="text-start mb-0">
                  <Row className="g-2">
                    <Col xs="auto">
                      <i className="fal fa-share-all fw-bold"> </i>
                    </Col>
                    <Col>
                      *FW* will show all seasons whose ID contains FW anywhere
                    </Col>
                  </Row>
                </p>
              </Tooltip>
            </Col>
          </Row>
        </Permission>
      </Col>
      <Col md="4" className="mt-3 mt-md-0 d-flex justify-content-end">
        <Permission
          allowed={Rules.Control.ADD_SEASON}
        >
          <Button color="primary" size="lg" className="btn-addnew" onClick={() => toggleAddModal()}>
            <i className="fal fa-plus me-2"> </i>
            Add New Season
          </Button>
        </Permission>
      </Col>
      <AddSeason
        addModal={addModal}
        toggleAddModal={toggleAddModal}
        handleCloseAddModal={handleCloseAddModal}
        season={season}
        handleChange={handleChange}
        submitSeasonForm={submitSeasonForm}
        handleSubmit={handleSubmit}
        register={register}
        errors={errors}
        handleDateChange={handleDateChange}
        message={message}
        addSeasonInProgress={addSeasonInProgress}
      />
    </Row>
  );
};

Search.propTypes = {
  addModal: PropTypes.bool.isRequired,
  toggleAddModal: PropTypes.func.isRequired,
  handleCloseAddModal: PropTypes.func.isRequired,
  season: PropTypes.objectOf(PropTypes.any).isRequired,
  handleChange: PropTypes.func.isRequired,
  submitSeasonForm: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.objectOf(PropTypes.any).isRequired,
  handleDateChange: PropTypes.func.isRequired,
  searchTerm: PropTypes.string.isRequired,
  handleSearchTerm: PropTypes.func.isRequired,
  handleSearch: PropTypes.func.isRequired,
  handleKeyPress: PropTypes.func.isRequired,
  message: PropTypes.objectOf(PropTypes.any).isRequired,
  addSeasonInProgress: PropTypes.bool.isRequired,
};

export default Search;
